import React, { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "@components/auth/ProtectedRoute";
import { TenantLayout } from "@components/layout/TenantLayout";
import { GlobalLoader } from "@components/loader";
import { NotFoundPage } from "@pages/NotFoundPage";

const lazyPage = <K extends string>(loader: () => Promise<Record<K, React.ComponentType>>, name: K) =>
    React.lazy(() => loader().then((m) => ({ default: m[name] })));

const Login = lazyPage(() => import("@pages/Login"), "Login");
const ForgotPassword = lazyPage(() => import("@pages/ForgotPassword"), "ForgotPassword");
const Otp = lazyPage(() => import("@pages/Otp"), "Otp");
const ResetPassword = lazyPage(() => import("@pages/ResetPassword"), "ResetPassword");
const AreasPage = lazyPage(() => import("@pages/AreasPage"), "AreasPage");
const RidersPage = lazyPage(() => import("@pages/RidersPage"), "RidersPage");
const RiderDetailPage = lazyPage(() => import("@pages/RiderDetailPage"), "RiderDetailPage");
const CustomersPage = lazyPage(() => import("@pages/CustomersPage"), "CustomersPage");
const CustomerDetailPage = lazyPage(() => import("@pages/CustomerDetailPage"), "CustomerDetailPage");
const PaymentsPage = lazyPage(() => import("@pages/PaymentsPage"), "PaymentsPage");
const ProductsPage = lazyPage(() => import("@pages/ProductsPage"), "ProductsPage");
const ProductDetailPage = lazyPage(() => import("@pages/ProductDetailPage"), "ProductDetailPage");
const StockPage = lazyPage(() => import("@pages/StockPage"), "StockPage");
const ShiftsPage = lazyPage(() => import("@pages/ShiftsPage"), "ShiftsPage");
const ShiftDetailPage = lazyPage(() => import("@pages/ShiftDetailPage"), "ShiftDetailPage");
const DeliveriesPage = lazyPage(() => import("@pages/DeliveriesPage"), "DeliveriesPage");
const MovementsPage = lazyPage(() => import("@pages/MovementsPage"), "MovementsPage");
const DashboardPage = lazyPage(() => import("@pages/DashboardPage"), "DashboardPage");
const ReportsPage = lazyPage(() => import("@pages/ReportsPage"), "ReportsPage");
const SupportPage = lazyPage(() => import("@pages/SupportPage"), "SupportPage");
const SupportTicketPage = lazyPage(() => import("@pages/SupportTicketPage"), "SupportTicketPage");
const ProfilePage = lazyPage(() => import("@pages/ProfilePage"), "ProfilePage");
const SettingsPage = lazyPage(() => import("@pages/SettingsPage"), "SettingsPage");
const SubscriptionPage = lazyPage(() => import("@pages/SubscriptionPage"), "SubscriptionPage");
const BillingReturnPage = lazyPage(() => import("@pages/BillingReturnPage"), "BillingReturnPage");
const RegisterPage = lazyPage(() => import("@pages/RegisterPage"), "RegisterPage");
const OnboardingPage = lazyPage(() => import("@pages/OnboardingPage"), "OnboardingPage");
const PaymentCancelledPage = lazyPage(() => import("@pages/PaymentCancelledPage"), "PaymentCancelledPage");
const NotificationsPage = lazyPage(() => import("@pages/NotificationsPage"), "NotificationsPage");

export const Router: React.FC = () => (
    <Suspense fallback={<GlobalLoader />}>
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/otp" element={<Otp />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/abort" element={<PaymentCancelledPage />} />
            <Route
                element={
                    <ProtectedRoute>
                        <TenantLayout />
                    </ProtectedRoute>
                }
            >
                <Route path="areas" element={<AreasPage />} />
                <Route path="riders" element={<RidersPage />} />
                <Route path="riders/:id" element={<RiderDetailPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="customers/:id" element={<CustomerDetailPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/:id" element={<ProductDetailPage />} />
                <Route path="stocks" element={<StockPage />} />
                <Route path="shifts" element={<ShiftsPage />} />
                <Route path="shifts/:id" element={<ShiftDetailPage />} />
                <Route path="deliveries" element={<DeliveriesPage />} />
                <Route path="movements" element={<MovementsPage />} />
                <Route index element={<DashboardPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="support/:id" element={<SupportTicketPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="subscription" element={<SubscriptionPage />} />
                <Route path="billing/return" element={<BillingReturnPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    </Suspense>
);
